import { useDispatch } from 'react-redux';
import { useTranslate } from 'react-translate.ts';
import { Button } from '@tcc-online/common-components';

import { IAuthTokens } from '../../../types';
import { AuthService } from '../../../services/AuthService';
import { AuthActions } from '../../../actions';
import Building from 'react-bootstrap-icons/dist/icons/building';
import styles from './CustomerSelect.module.scss';
import { css, cx } from '@emotion/css';
import { useTheme } from '@tcc-online/react-theme-provider';
import AuthCard from '../AuthCard/AuthCard';

type Props = {
    tokens: IAuthTokens[];
};

export function CustomerSelect({ tokens }: Props) {
    const { backToLogin, selectCustomer } = mapDispatch(useDispatch());
    const translate = useTranslate('fe.login');
    const [theme] = useTheme();

    return (
        <AuthCard
            isLoading={false}
            header={
                <div>
                    <h5
                        className={cx(
                            styles.h,
                            css({ color: theme.primaryColor }),
                        )}
                    >
                        {translate('title')}
                    </h5>
                    <p>{translate('company.hint')}</p>
                </div>
            }
            footer={
                <Button isTransparent onClick={backToLogin}>
                    {translate('company.back')}
                </Button>
            }
        >
            <div className={styles.list}>{tokens.map(renderCustomer)}</div>
        </AuthCard>
    );

    function renderCustomer({ accessToken, refreshToken }, key: number) {
        const customer = AuthService.decodeUserData(accessToken);
        if (!customer) {
            return null;
        }

        return (
            <CustomerItem
                key={key}
                customer={customer}
                onSelect={() => selectCustomer(accessToken, refreshToken)}
            />
        );
    }
}

function CustomerItem({ customer, onSelect }) {
    const { company, roles, companyLogo } = customer;
    const [theme] = useTheme();

    return (
        <div
            className={cx(
                styles.item,
                css({
                    '&:hover': {
                        background: theme.backgroundColor,
                    },
                }),
            )}
            onClick={onSelect}
        >
            {companyLogo ? (
                <img className={styles.logo} src={companyLogo} alt="" />
            ) : (
                <Building className={styles.logo} />
            )}
            <div className={styles.name}>{company}</div>
            {isManager(roles) ? <RoleLabel role={roles[0]} /> : null}
        </div>
    );
}

function RoleLabel({ role }: { role: string }) {
    const translate = useTranslate('common.model.uzivatel.role');
    const [theme] = useTheme();

    return (
        <div
            className={cx(
                styles.role,
                css({
                    color: theme.surfaceColor,
                    background: theme.primaryColor,
                }),
            )}
        >
            {translate(role)}
        </div>
    );
}

function isManager(roles: string[]) {
    return roles.includes('manager');
}

function mapDispatch(dispatch: any) {
    return {
        backToLogin: () => dispatch(AuthActions.backToLogin()),
        selectCustomer: (accessToken: string, refreshToken: string) =>
            dispatch(AuthActions.selectCustomer(accessToken, refreshToken)),
    };
}
